import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FaCalendarAlt, FaEye, FaTimes } from "react-icons/fa";
import styles from "../FeeType/styles.module.css";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, {
  TokenService,
} from "../../../../services/Api";
import BlobImage from "../../../../components/BlobImage/BlobImage";
import {  CustomDatePicker } from "../../../../components";
import dayjs from "dayjs";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import { useSelector } from "react-redux";

const PAYMENT_APPROVAL_LIST_URL = "schoolFees/paymentApproval/list";
const PAYMENT_APPROVE_URL = "schoolFees/paymentApprove";

// status sent to paymentApprove
const APPROVE_STATUS = 1;
const REJECT_STATUS = 2;

/* ── Inline styles ── */
const S = {
  viewBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 34,
    height: 34,
    background: "#f97316",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  dateInput: {
    width: "100%",
    padding: "10px 12px",
    fontSize: 14,
    border: "1px solid #d1d5db",
    borderRadius: 6,
    outline: "none",
    color: "#1e3a5f",
  },
  dateLabel: {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: "#374151",
    marginBottom: 6,
  },
  // ── receipt modal ──
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1200,
    padding: 16,
  },
  modal: {
    background: "#fff",
    borderRadius: 10,
    width: "100%",
    maxWidth: 600,
    maxHeight: "92vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTop: "6px solid #1e3a5f",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "flex-end",
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 20,
    cursor: "pointer",
    color: "#1e293b",
    lineHeight: 1,
  },
  receiptArea: {
    flex: 1,
    overflowY: "auto",
    padding: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    background: "#fff",
    minHeight: 320,
  },
  receiptImg: {
    maxWidth: "100%",
    height: "auto",
    borderRadius: 6,
  },
  pdfFrame: {
    width: "100%",
    height: 460,
    border: "none",
  },
  emptyReceipt: {
    color: "#6b7280",
    fontSize: 14,
    padding: 60,
    textAlign: "center",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "center",
    gap: 16,
    padding: "16px 20px 20px",
    borderTop: "1px solid #e5e7eb",
  },
  rejectBtn: {
    padding: "12px 34px",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "#1e3a5f",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  approveBtn: {
    padding: "12px 34px",
    fontSize: 15,
    fontWeight: 600,
    color: "#fff",
    background: "#f97316",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
  },
  disabledBtn: { opacity: 0.6, cursor: "not-allowed" },
  // ── column header filters ──
  headerDateWrap: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 180,
  },
  headerInput: {
    width: "100%",
    padding: "4px 6px",
    fontSize: 13,
    border: "none",
    borderBottom: "1px solid #d1d5db",
    background: "transparent",
    outline: "none",
    color: "#1e3a5f",
  },
};

/* ── ₹23,900 — paid_amount arrives as a string ("23900.00") ── */
const formatCurrency = (val) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);

/* ────────────────────────────────────────────
   Receipt modal — renders payment_file_path.
   Images inline; PDFs in an iframe.
──────────────────────────────────────────── */
const ReceiptModal = ({ open, row, onClose, onApprove, onReject, busy }) => {
  if (!open || !row) return null;

  const filePath = row.payment_file_path || "";

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <button
            type="button"
            style={S.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        <div style={S.receiptArea}>
          {!filePath ? (
            <div style={S.emptyReceipt}>No receipt attached</div>
          ) : (
            <BlobImage
              path={filePath}
              alt={row.payment_original_path || "Payment receipt"}
              style={S.receiptImg}
            />
          )}
        </div>

        <div style={S.modalFooter}>
          <button
            type="button"
            style={{
              ...S.rejectBtn,
              ...(busy ? S.disabledBtn : {}),
            }}
            disabled={busy}
            onClick={() => onReject(row)}
          >
            Reject
          </button>
          <button
            type="button"
            style={{
              ...S.approveBtn,
              ...(busy ? S.disabledBtn : {}),
            }}
            disabled={busy}
            onClick={() => onApprove(row)}
          >
            Approve
          </button>
        </div>
      </div>
    </div>
  );
};

const ListPaymentApproval = () => {
  const { translations } = useSelector((state) => state.language);

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  const academic_year_id =
    clientData?.academic_year_id || clientData?.academic_id || "";
  const user_id = user?.id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ── Filters that feed the payload ──
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ── Raw text typed into the Class / Section header inputs ──
  const [classInput, setClassInput] = useState("");
  const [sectionInput, setSectionInput] = useState("");

  // ── Receipt modal ──
  const [receiptRow, setReceiptRow] = useState(null);
  const [approving, setApproving] = useState(false);

  const [modalState, setModalState] = useState({
    open: false,
    type: "",
    message: "",
  });

  // ── Debounce the Class / Section inputs into the payload filters ──
  useEffect(() => {
    const timer = setTimeout(() => {
      const nextClass = classInput.trim();
      const nextSection = sectionInput.trim();
      if (nextClass === selectedClass && nextSection === selectedSection) {
        return;
      }
      setSelectedClass(nextClass);
      setSelectedSection(nextSection);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, 500);
    return () => clearTimeout(timer);
  }, [classInput, sectionInput, selectedClass, selectedSection]);

  /* ────────────────────────────────────────────
       API BINDING — schoolFees/paymentApproval/list
    ──────────────────────────────────────────── */
  const fetchPaymentApprovalList = useCallback(async () => {
    if (!school_id) {
      setData([]);
      setRowCount(0);
      return;
    }

    setLoading(true);
    try {
      const response = await AxiosInstance.post(PAYMENT_APPROVAL_LIST_URL, {
        school_id: Number(school_id),
        academic_year_id: Number(academic_year_id),
        // class filter is free text (e.g. "V") — send as typed, not Number()
        class_id: selectedClass || "",
        section_id: selectedSection || "",
        start_date: startDate || "",
        end_date: endDate || "",
        search: apiSearch.trim(),
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      });

      if (response?.data?.status && Array.isArray(response.data.data)) {
        const transformed = response.data.data.map((item) => ({
          payment_id: item.payment_id,
          admission_id: item.admission_id || "",
          student_name: item.student_name || "",
          class_name: item.class_name || "",
          section: item.section || "",
          mobile_number: item.mobile_number || "",
          submitted_date: item.submitted_date || "",
          upi_reference_id: item.upi_reference_id || "",
          // arrives as a string, e.g. "23900.00"
          paid_amount: Number(item.paid_amount) || 0,
          payment_file_path: item.payment_file_path || "",
          payment_original_path: item.payment_original_path || "",
        }));
        setData(transformed);
        setRowCount(response.data.pagination?.total || transformed.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching payment approval list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    school_id,
    academic_year_id,
    selectedClass,
    selectedSection,
    startDate,
    endDate,
    apiSearch,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  useEffect(() => {
    fetchPaymentApprovalList();
  }, [fetchPaymentApprovalList]);

  // ── Handlers ──
  const handleSearchEnter = useCallback((value) => {
    setApiSearch(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // View -> open the receipt modal for this row
  const handleView = useCallback((row) => setReceiptRow(row), []);

  const closeReceipt = useCallback(() => {
    if (approving) return; // don't close mid-request
    setReceiptRow(null);
  }, [approving]);

  /* ── Approve / Reject ──
       POST schoolFees/paymentApprove
       { id: payment_id, status: 1 | 0, updated: user_id }
       NOTE: the payload key is `updated` (not updated_by). */
  const submitApproval = useCallback(
    async (row, status) => {
      if (!row?.payment_id) return;
      setApproving(true);
      try {
        const response = await AxiosInstance.post(PAYMENT_APPROVE_URL, {
          id: Number(row.payment_id),
          status: status,
          updated: Number(user_id),
        });

        const ok = response?.data?.status || response?.data?.success;

        if (ok) {
          setReceiptRow(null);
          setModalState({
            open: true,
            type: "success",
            message:
              response?.data?.message ||
              (status === APPROVE_STATUS
                ? "Payment approved successfully!"
                : "Payment rejected."),
          });
          // the row leaves the pending queue -> refresh
          fetchPaymentApprovalList();
        } else {
          setModalState({
            open: true,
            type: "error",
            message:
              response?.data?.message ||
              translations.labels?.somethingWrong ||
              "Something went wrong!",
          });
        }
      } catch (error) {
        console.error("Error updating payment approval:", error);
        setModalState({
          open: true,
          type: "error",
          message:
            error?.response?.data?.message ||
            "Failed to update the payment. Please try again.",
        });
      } finally {
        setApproving(false);
      }
    },
    [user_id, fetchPaymentApprovalList, translations],
  );

  const handleApprove = useCallback(
    (row) => submitApproval(row, APPROVE_STATUS),
    [submitApproval],
  );
  const handleReject = useCallback(
    (row) => submitApproval(row, REJECT_STATUS),
    [submitApproval],
  );
const formatDate = (val) => {
  if (!val) return "-";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(val);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return val;
};
  // ── Columns ──
  const columns = useMemo(
    () => [
      {
        accessorKey: "admission_id",
        header: translations.labels?.admissionId || "Admission ID",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_name",
        header: translations.labels?.studentName || "Student Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "class_name",
        header: translations.labels?.class || "Class",
        enableSorting: true,
        enableColumnFilter: true,
        // Server-side: classInput -> (debounced) selectedClass -> class_id
        Filter: () => (
          <input
            type="text"
            value={classInput}
            onChange={(e) => setClassInput(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={translations.labels?.filterByClass || "Filter by Class"}
            style={S.headerInput}
          />
        ),
      },
      {
        accessorKey: "section",
        header: translations.labels?.section || "Section",
        enableSorting: true,
        enableColumnFilter: true,
        // Server-side: sectionInput -> (debounced) selectedSection -> section_id
        Filter: () => (
          <input
            type="text"
            value={sectionInput}
            onChange={(e) => setSectionInput(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            placeholder={
              translations.labels?.filterBySection || "Filter by Section"
            }
            style={S.headerInput}
          />
        ),
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "mobile_number",
        header: translations.labels?.mobileNumber || "Mobile Number",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "submitted_date",
        header: translations.labels?.submittedDate || " Submitted Date",
        enableSorting: true,
        enableColumnFilter: true,
        /* Server-side. The payload takes a RANGE, so the header
                   filter is a from/to pair writing to startDate/endDate. */
        Filter: () => {
          const startValue = startDate ? dayjs(startDate) : null;
          const endValue = endDate ? dayjs(endDate) : null;
          return (
            <div style={S.headerDateWrap} onClick={(e) => e.stopPropagation()}>
              <CustomDatePicker
                name="due_date_start"
                value={startValue}
                onChange={(d) => {
                  setStartDate(d ? dayjs(d).format("YYYY-MM-DD") : "");
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
                placeholder={
                  translations.labels?.startDatePlaceholder || "Start Date"
                }
                format="DD/MM/YYYY"
                size="sm"
                isClearable={true}
                maxDate={endValue || undefined}
              />
              <CustomDatePicker
                name="due_date_end"
                value={endValue}
                onChange={(d) => {
                  setEndDate(d ? dayjs(d).format("YYYY-MM-DD") : "");
                  setPagination((p) => ({ ...p, pageIndex: 0 }));
                }}
                placeholder={
                  translations.labels?.endDatePlaceholder || "End Date"
                }
                format="DD/MM/YYYY"
                size="sm"
                isClearable={true}
                minDate={startValue || undefined}
              />
            </div>
          );
        },
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "upi_reference_id",
        header: translations.labels?.upiReferenceId || "UPI Reference ID",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "paid_amount",
        header: translations.labels?.paidAmount || "Paid Amount",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatCurrency(cell.getValue()),
      },
      {
        id: "actions",
        header: translations.labels?.action || "Action",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            type="button"
            style={S.viewBtn}
            onClick={() => handleView(row.original)}
            title="View receipt"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [
      translations,
      handleView,
      startDate,
      endDate,
      classInput,
      sectionInput,
    ],
  );

  return (
    <div className={styles.container}>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? translations.labels?.success || "Success"
            : translations.labels?.failed || "Failed"
        }
        message={modalState.message}
        onOk={() => setModalState((prev) => ({ ...prev, open: false }))}
      />

      <ReceiptModal
        open={Boolean(receiptRow)}
        row={receiptRow}
        onClose={closeReceipt}
        onApprove={handleApprove}
        onReject={handleReject}
        busy={approving}
      />

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>
          {translations.labels?.paymentApproval || "Payment Approval"}
        </h1>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        placeholder={translations?.labels?.searchNameCode || "Search ..."}
        setSearchInput={setSearchInput}
        onSearchEnter={handleSearchEnter}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        enableSearch={true}
        enableFiltering={true}
        manualPagination={true}
      />
    </div>
  );
};

export default ListPaymentApproval;
